import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import {
  usePutCoachTipList,
  usePutDietPlanList,
  usePutDraftHeaderDataList,
  usePutHomePageTagLineList,
  usePutRootCauseList,
  usePutRootCauseSectionList,
  usePutTaskList,
  usePutTrayaDoctorList,
  usePutUserReviewList,
  usePutWelcomeVideoList,
} from "./store";
import { useToast } from "modules/navbar/top/store/toasterStore";
import { disablledKeysCMS, notToShowInCMS } from "../../config";

const EditableCard = ({ data, onUpdate, notToShowInCMS, disablledKeysCMS }) => {
  const [formData, setFormData] = useState(data);
  const [hasEdits, setHasEdits] = useState(false);

  const handleInputChange = (event, key) => {
    const updatedData = {
      ...formData,
      [key]: event.target.value,
    };
    setFormData(updatedData);
    setHasEdits(!isEqual(updatedData, data)); // Check for edits
  };

  const handleSaveEdits = () => {
    onUpdate(formData);
    setHasEdits(false); // Reset the edit status after saving
  };

  const isEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  return (
    <Card style={{ width: "25rem" }}>
      {(formData.urlOrText ||
        formData.imageUrl ||
        formData.image ||
        formData.icon ||
        formData.profileImage ||
        formData.images) && (
        <Card.Img
          className="w-100 img-thumbnail"
          style={{ width: "100%", height: "300px" }}
          src={
            formData.urlOrText ||
            formData.imageUrl ||
            formData.image ||
            formData.icon ||
            formData.profileImage ||
            formData.images
          }
          variant="top"
        />
      )}

      <Card.Body>
        {Object.keys(formData).map((key) => {
          if (!notToShowInCMS.includes(key)) {
            return (
              <div key={key}>
                <Form.Label>{key}</Form.Label>
                <input
                  type="text"
                  disabled={disablledKeysCMS.includes(key)}
                  value={
                    typeof formData[key] === "object"
                      ? JSON.stringify(formData[key])
                      : formData[key]
                  }
                  onChange={(event) => handleInputChange(event, key)}
                  className="form-control"
                />
              </div>
            );
          }
          return null;
        })}
      </Card.Body>
      <Card.Body>
        <Button variant="primary" onClick={handleSaveEdits} disabled={!hasEdits}>
          Save Edits
        </Button>
      </Card.Body>
    </Card>
  );
};

const MultipleEditableCards = ({ data, activeKey }) => {
  const { updateTaskList } = usePutTaskList();
  const { updateCoachTipList } = usePutCoachTipList();
  const { updateDietPlanList } = usePutDietPlanList();
  const { updateHomePageTagLineList } = usePutHomePageTagLineList();
  const { updatePutRootCauseList } = usePutRootCauseList();
  const { updateRootCauseSectionList } = usePutRootCauseSectionList();
  const { updateTrayaDoctorList } = usePutTrayaDoctorList();
  const { updateDraftHeaderDataList } = usePutDraftHeaderDataList();
  const { updateWelcomeVideoList } = usePutWelcomeVideoList();
  const { updateUserReviewList } = usePutUserReviewList();
  const { setToaster } = useToast();

  const updateCardData = (index, updatedData) => {
    let extractedData;

    if (activeKey === "task-list") {
      extractedData = {
        h1: updatedData.h1,
        h2: updatedData.h2,
        h3: [],
        eventName: Array.isArray(updatedData.eventName)
          ? updatedData.eventName[0]
          : "",
        urlOrText: updatedData.urlOrText,
      };
      updateTaskList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "coach-tip-list") {
      extractedData = {
        coachName: updatedData.coachName,
        imageUrl: updatedData.imageUrl,
        tip: updatedData.tip,
        isActive: updatedData.isActive,
      };
      updateCoachTipList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "diet-plan-list") {
      extractedData = {
        content: updatedData.content,
        imageUrl: updatedData.imageUrl,
        isDietPlanExist: updatedData.isDietPlanExist,
        isActive: updatedData.isActive,
      };
      updateDietPlanList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "home-page-tag-line-list") {
      extractedData = {
        text: updatedData.text,
        isActive: updatedData.isActive,
      };
      updateHomePageTagLineList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "root-cause-list") {
      extractedData = {
        rootCauseName: updatedData.rootCauseName,
        icon: updatedData.icon,
        video: updatedData.video,
        isActive: updatedData.isActive,
      };
      updatePutRootCauseList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "reorder-kit-list") {
      extractedData = {
        title: updatedData.title,
        description: updatedData.description,
        minReorderDays: updatedData.minReorderDays,
        maxReorderDays: updatedData.maxReorderDays,
        isActive: updatedData.isActive,
      };
      updatePutRootCauseList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "root-cause-section-list") {
      extractedData = {
        title: updatedData.title,
        isActive: updatedData.isActive,
      };
      updateRootCauseSectionList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "doctor-list") {
      extractedData = {
        doctorName: updatedData.doctorName,
        profileImage: updatedData.profileImage,
        experience: updatedData.experience,
        description: updatedData.description,
        video: updatedData.video,
      };
      updateTrayaDoctorList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "draft-header-data-list") {
      extractedData = {
        doctorName: updatedData.doctorName,
        profileImage: updatedData.profileImage,
        experience: updatedData.experience,
        description: updatedData.description,
        video: updatedData.video,
        cta: updatedData.cta,
        ctaUpperText: updatedData.ctaUpperText,
      };
      updateDraftHeaderDataList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "welcome-video-list") {
      extractedData = {
        title: updatedData.title,
        image: updatedData.image,
        swirlId: updatedData.swirlId,
      };
      updateWelcomeVideoList(extractedData, updatedData.id, setToaster);
    } else if (activeKey === "user-review-list") {
      extractedData = {
        reviewerName: updatedData.reviewerName,
        description: updatedData.description,
        platform: updatedData.platform,
        reviewUrl: updatedData.reviewUrl,
        video: updatedData.video,
        platform: updatedData.platform,
      };
      updateUserReviewList(extractedData, updatedData.id, setToaster);
    }
  };

  return (
    <div className="container">
      <div className="row">
        {data.map((item, index) => (
          <div className="col-md-3 mb-3" key={index}>
            <EditableCard
              data={item}
              onUpdate={(updatedData) => updateCardData(index, updatedData)}
              notToShowInCMS={notToShowInCMS}
              disablledKeysCMS={disablledKeysCMS}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultipleEditableCards;